import {
  useConfirmUserAttribute,
  useSendUserAttributeVerificationCode,
  useUpdateMfaPreference,
  useUpdateUserAttributes,
} from 'korint-sdk/react';
import { useEffect, useState } from 'react';
import { type Control, type FieldValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

import { toE164 } from '../../../lib/phone-input';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../forms/form';
import { FormPhoneNumberInput } from '../../forms/form-phone-number-input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../forms/input-otp';
import { Text } from '@folio/components/ui/text';

export function SmsSetupForm({ onSuccess, onBack }: { onSuccess: () => void; onBack: () => void }) {
  const { t } = useTranslation();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const { updateUserAttributes } = useUpdateUserAttributes();
  const { sendUserAttributeVerificationCode } = useSendUserAttributeVerificationCode();
  const { confirmUserAttribute, isLoading: isVerifying } = useConfirmUserAttribute({
    onError: err => setError(mapCodeError(err, t)),
  });
  const { updateMFAPreference } = useUpdateMfaPreference({
    onError: err => setError(mapCodeError(err, t)),
  });

  const phoneForm = useForm<{ phoneNumber: string }>({
    defaultValues: { phoneNumber: '' },
    resolver: zodResolver(
      z.object({
        // SMS MFA is currently French-numbers only. isValidPhoneNumber(v, 'FR')
        // would NOT reject this: 'FR' is only a fallback for ambiguous
        // national-format input, not a hard restriction — a number with an
        // explicit non-FR country code still comes back valid. Check the
        // parsed country instead.
        phoneNumber: z.string().refine(
          v => {
            const parsed = parsePhoneNumberFromString(v, 'FR');
            return !!parsed && parsed.isValid() && parsed.country === 'FR';
          },
          { message: t('authSmsSetup.phoneInvalid') },
        ),
      }),
    ),
  });

  const handlePhoneSubmit = phoneForm.handleSubmit(async ({ phoneNumber }) => {
    setError(null);
    const e164 = toE164(phoneNumber);
    try {
      await updateUserAttributes({ userAttributes: { phone_number: e164 } });
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      setSentTo(e164);
      setCooldown(60);
    } catch (err) {
      setError(mapPhoneError(err, t));
    }
  });

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    setResendError(null);
    setIsResending(true);
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      setCooldown(60);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : t('authSmsSetup.errorGeneric'));
    } finally {
      setIsResending(false);
    }
  };

  const codeForm = useForm<{ code: string }>({
    defaultValues: { code: '' },
    resolver: zodResolver(z.object({ code: z.string().length(6) })),
  });

  const handleCodeSubmit = codeForm.handleSubmit(async ({ code }) => {
    setError(null);
    try {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: code });
      await updateMFAPreference({ sms: 'PREFERRED' });
      onSuccess();
    } catch {}
  });

  if (!sentTo) {
    return (
      <Form {...phoneForm}>
        <Text size="sm" tone="muted" className="mb-4">
          {t('authSmsSetup.phoneSubtitle')}
        </Text>
        {error && (
          <Alert className="mb-4" variant="destructive">
            {error}
          </Alert>
        )}
        <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-3">
          <FormPhoneNumberInput
            control={phoneForm.control as unknown as Control<FieldValues>}
            name="phoneNumber"
            label={t('authSmsSetup.phoneLabel')}
            placeholder="6 12 34 56 78"
            countries={['FR']}
            required
          />
          <Button className="w-full" type="submit" loading={phoneForm.formState.isSubmitting}>
            {t('authSmsSetup.sendCode')}
          </Button>
          <Button className="w-full" type="button" variant="ghost" onClick={onBack}>
            {t('authMfaSetup.back')}
          </Button>
        </form>
      </Form>
    );
  }

  return (
    <Form {...codeForm}>
      <Text size="sm" tone="muted" className="mb-4">
        {t('authSmsSetup.codeSubtitle', { destination: sentTo })}
      </Text>
      {resendError && (
        <Alert className="mb-4" variant="destructive">
          {resendError}
        </Alert>
      )}
      {error && (
        <Alert className="mb-4" variant="destructive">
          {error}
        </Alert>
      )}
      <form onSubmit={handleCodeSubmit} className="flex flex-col items-center gap-4">
        <FormField
          control={codeForm.control}
          name="code"
          render={({ field }) => (
            <FormItem className="items-center">
              <FormControl>
                <InputOTP maxLength={6} autoFocus {...field}>
                  <InputOTPGroup>
                    {Array.from({ length: 6 }, (_, index) => (
                      <InputOTPSlot key={`sms-setup-slot-${index}`} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="w-full"
          type="submit"
          loading={isVerifying}
          disabled={codeForm.watch('code').length < 6}
        >
          {t('authSmsSetup.submit')}
        </Button>
        <Button
          className="w-full"
          type="button"
          variant="outline"
          onClick={handleResend}
          disabled={cooldown > 0 || isResending}
          loading={isResending}
        >
          {cooldown > 0
            ? t('authSmsChallenge.resendCooldown', { seconds: cooldown })
            : t('authSmsChallenge.resend')}
        </Button>
      </form>
    </Form>
  );
}

function mapPhoneError(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) {
    return t('authSmsSetup.errorGeneric');
  }
  const name = (err as Error & { name?: string }).name ?? '';
  if (name === 'InvalidParameterException') return t('authSmsSetup.errorInvalidPhone');
  return err.message || t('authSmsSetup.errorGeneric');
}

function mapCodeError(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) {
    return t('authSmsSetup.errorGeneric');
  }
  const name = (err as Error & { name?: string }).name ?? '';
  if (name === 'CodeMismatchException') return t('authTotpSetup.errorInvalidCode');
  if (name === 'ExpiredCodeException') return t('authTotpSetup.errorCodeExpired');
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return t('authTotpSetup.errorRateLimited');
  }
  if (name === 'CodeDeliveryFailureException') return t('authSmsSetup.errorDeliveryFailed');
  return err.message || t('authSmsSetup.errorGeneric');
}
